import axios from "axios";

//const API_URL = 'http://localhost:5000/api';

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : `${process.env.REACT_APP_API_URL || "http://192.168.68.67:5000"}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set Content-Type based on data type
    // If it's FormData, let browser set the Content-Type (for file uploads)
    // Otherwise, use application/json
    if (!(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Handle 401 errors globally (expired/invalid tokens, deleted accounts)
api.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  (error) => {
    // If we get a 401 error, user is not authenticated
    if (error.response && error.response.status === 401) {
      // Don't intercept 401 errors from login/register endpoints
      const isAuthEndpoint =
        error.config?.url?.includes("/auth/login") ||
        error.config?.url?.includes("/auth/register");

      if (isAuthEndpoint) {
        // Let login page handle its own errors
        return Promise.reject(error);
      }

      // Clear the token
      localStorage.removeItem("token");

      // Check if it's a deleted account
      const isAccountDeleted = error.response.data?.accountDeleted;

      // Redirect to login with appropriate message
      if (isAccountDeleted) {
        window.location.href = "/login?reason=account-deleted";
      } else {
        window.location.href = "/login?reason=session-expired";
      }
    }
    // For all other errors, just reject normally
    return Promise.reject(error);
  },
);

// Auth endpoints
const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, password) =>
    api.post("/auth/reset-password", { token, password }),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  getInviteDetails: (token, teamId) =>
    api.get(`/coaches/invite/${token}?teamId=${teamId}`),
  acceptInvite: (data) => api.post("/coaches/accept-invite", data),
};

// Team endpoints
const teamAPI = {
  create: (data) => api.post("/teams", data),
  getAll: async () => {
    const response = await api.get("/teams");
    // Handle both formats: array (old) or object with teams array (new)
    if (Array.isArray(response.data)) {
      // Old format - return as-is
      return response;
    } else {
      // New format - return wrapped for backward compatibility
      return {
        ...response,
        data: response.data.teams || [],
        hasLockedOrgTeams: response.data.hasLockedOrgTeams || false,
        lockedOrgTeamsCount: response.data.lockedOrgTeamsCount || 0,
      };
    }
  },
  getById: (id) => api.get(`/teams/${id}`),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  getLimitInfo: () => api.get("/teams/limit-info"),
};

// Graphic endpoints
const graphicAPI = {
  save: (data) => api.post("/graphics", data),
  update: (id, data) => api.put(`/graphics/${id}`, data),
  getAll: () => api.get("/graphics"),
  getById: (id) => api.get(`/graphics/${id}`),
  delete: (id) => api.delete(`/graphics/${id}`),
  getLimitInfo: () => api.get("/graphics/limit-info"),
};

// Roster endpoints
const rosterAPI = {
  getTeamRoster: (teamId) => api.get(`/roster/team/${teamId}`),
  createPlayer: (data) => api.post("/roster", data),
  bulkImport: (data) => api.post("/roster/bulk", data),
  updatePlayer: (id, data) => api.put(`/roster/${id}`, data),
  deletePlayer: (id) => api.delete(`/roster/${id}`),
};

// Coach endpoints
const coachAPI = {
  inviteCoach: (teamId, coachEmail) =>
    api.post(`/coaches/team/${teamId}/invite`, { coachEmail }),
  getTeamCoaches: (teamId) => api.get(`/coaches/team/${teamId}/coaches`),
  removeCoach: (teamId, coachId) =>
    api.delete(`/coaches/team/${teamId}/coach/${coachId}`),
  resendInvite: (teamId, userId) =>
    api.post(`/coaches/team/${teamId}/resend-invite/${userId}`),
  assignSelfAsCoach: (teamId) =>
    api.post(`/coaches/teams/${teamId}/assign-self`),
};

// Organization endpoints
const organizationAPI = {
  getDefaults: () => api.get("/organization/defaults"),
  updateDefaults: (data) => api.put("/organization/defaults", data),
  bulkCreateTeams: (teams) => api.post("/teams/bulk-create", { teams }),
};

// Trash endpoints
const trashAPI = {
  getRecentlyDeleted: () => api.get("/trash"),
  restoreTeam: (id) => api.post(`/trash/teams/${id}/restore`),
  restoreGraphic: (id) => api.post(`/trash/graphics/${id}/restore`),
  restorePlayer: (id) => api.post(`/trash/players/${id}/restore`),
  restoreBragCard: (id) => api.post(`/trash/bragcards/${id}/restore`),
  permanentlyDeleteTeam: (id) => api.delete(`/trash/teams/${id}/permanent`),
  permanentlyDeleteGraphic: (id) =>
    api.delete(`/trash/graphics/${id}/permanent`),
  permanentlyDeleteBragCard: (id) =>
    api.delete(`/trash/bragcards/${id}/permanent`),
};

// Analytics endpoints
const analyticsAPI = {
  getPlayers: () => api.get("/analytics/players"),
  getPlayerGames: (playerId) => api.get(`/analytics/players/${playerId}/games`),
};

// Brag Card endpoints
const bragCardAPI = {
  getByPlayer: (playerId) => api.get(`/brag-cards?playerId=${playerId}`),
  getById: (id) => api.get(`/brag-cards/${id}`),
  save: (data) => api.post("/brag-cards", data),
  update: (id, data) => api.put(`/brag-cards/${id}`, data),
  delete: (id) => api.delete(`/brag-cards/${id}`),
};

// Export everything once at the bottom
export {
  authAPI,
  teamAPI,
  graphicAPI,
  rosterAPI,
  trashAPI,
  coachAPI,
  organizationAPI,
  analyticsAPI,
  bragCardAPI,
};

export default api;
